import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  CalendarDays, Users, TrendingUp, Bell, Wallet, Megaphone, ArrowRight, Activity
} from "lucide-react";
import { Link } from "react-router-dom";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const r = await api.get("/dashboard/stats");
      setStats(r.data);
    } catch (err) {
      console.error("Failed to load dashboard stats:", err);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const cards = [
    { icon: CalendarDays, label: "Today's appointments", value: stats?.appointments_today ?? "—", color: "var(--primary)" },
    { icon: Bell, label: "Upcoming follow-ups", value: stats?.upcoming_follow_ups ?? "—", color: "var(--accent-warm)" },
    { icon: Users, label: "Active patients", value: stats?.active_patients ?? "—", color: "var(--secondary)" },
    { icon: Megaphone, label: "New leads (7d)", value: stats?.new_leads_7d ?? "—", color: "var(--primary-hover)" },
    { icon: TrendingUp, label: "Revenue (30d)", value: stats ? `₹${stats.revenue_30d.toLocaleString()}` : "—", color: "var(--success)" },
    { icon: Wallet, label: "Pending payments", value: stats ? `₹${stats.pending_payments.toLocaleString()}` : "—", color: "var(--warning)" },
  ];

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[color:var(--muted)]">Overview</p>
          <h1 className="font-heading text-3xl md:text-4xl text-[color:var(--ink)] font-medium mt-1">Good day, {user?.name?.split(" ")[0]}</h1>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-sm text-[color:var(--muted)]">
          <Activity className="w-4 h-4" /> Live snapshot
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className="kpi-card" data-testid={`kpi-${c.label.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
            <div className="w-9 h-9 rounded-lg grid place-items-center mb-3" style={{ background: `${c.color}1A`, color: c.color }}>
              <c.icon className="w-4.5 h-4.5" strokeWidth={1.6} />
            </div>
            <p className="font-heading text-2xl text-[color:var(--ink)] font-medium leading-tight">{c.value}</p>
            <p className="text-xs text-[color:var(--muted)] mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-[color:var(--border)] p-6" data-testid="upcoming-appointments-section">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-heading text-lg font-medium text-[color:var(--ink)]">Upcoming appointments</h3>
            <Link to="/appointments" className="text-xs font-medium text-[color:var(--primary)] hover:underline flex items-center gap-1">View all <ArrowRight className="w-3 h-3" /></Link>
          </div>
          {!stats?.upcoming_appointments?.length ? (
            <p className="text-sm text-[color:var(--muted)] py-8 text-center">No upcoming appointments.</p>
          ) : (
            <ul className="divide-y divide-[color:var(--border)]">
              {stats.upcoming_appointments.map((a) => (
                <li key={a.id} className="py-3 flex items-center justify-between" data-testid={`upcoming-apt-${a.id}`}>
                  <div>
                    <p className="font-medium text-[color:var(--ink)] text-sm">{a.patient_name}</p>
                    <p className="text-xs text-[color:var(--muted)]">{new Date(a.scheduled_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</p>
                  </div>
                  <span className="text-xs px-2.5 py-1 rounded-full bg-[color:var(--primary-light)]/40 text-[color:var(--primary-hover)] capitalize">{a.mode?.replace("_"," ") || "in person"}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-[color:var(--border)] p-6" data-testid="recent-leads-section">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-heading text-lg font-medium text-[color:var(--ink)]">Recent leads</h3>
            <Link to="/leads" className="text-xs font-medium text-[color:var(--primary)] hover:underline">View</Link>
          </div>
          {!stats?.recent_leads?.length ? (
            <p className="text-sm text-[color:var(--muted)] py-6 text-center">No recent leads.</p>
          ) : (
            <ul className="space-y-3">
              {stats.recent_leads.map((l) => (
                <li key={l.id} className="flex items-start gap-3" data-testid={`recent-lead-${l.id}`}>
                  <span className="w-8 h-8 rounded-full bg-[color:var(--primary-light)] grid place-items-center text-[color:var(--primary-hover)] text-xs font-medium">{l.name?.[0] || "L"}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[color:var(--ink)] truncate">{l.name}</p>
                    <p className="text-xs text-[color:var(--muted)] truncate">{l.condition || l.source}</p>
                  </div>
                  <span className="text-[10px] tracking-wider uppercase text-[color:var(--muted)]">{l.stage}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
